// ============================================================
// Dışa aktarma (§11)
//
// Muhasebeciye gidecek ödeme listesi, resmî kuruma gidecek öğrenci
// listesi, sigortaya gidecek personel dökümü — hiçbiri üretilemiyordu.
//
// CSV tercih edildi çünkü muhasebecinin elindeki her program onu açar.
// Excel'e özel biçim (xlsx) kütüphane gerektirir ve taşınabilirliği
// azaltır.
//
// KVKK notu: dışa aktarılan dosya kurumun sorumluluğundan çıkıp
// kişinin bilgisayarına iner. Bu yüzden her aktarım denetim izine
// yazılır — "veri nereye gitti" sorusunun cevabı olsun.
// ============================================================

import { BUGUN, DONEM } from "../config.js";
import { B, subeBaglaminda, subeBulHerYerde } from "./context.js";
import { age } from "../lib/fmt.js";
import { alerjiler, veliler } from "./students.js";
import { aylikOzet, ogrenciKaydi } from "./attendance.js";
import { faturaDurumu, faturaTutari, tahsilEdilen } from "./invoices.js";
import { bordroUret } from "./payroll.js";
import { donemGiderleri } from "./expenses.js";
import { personelBelgeleri, belgeDurumu } from "./documents.js";

/**
 * CSV üretimi.
 * Excel Türkçe yerelde noktalı virgülü ayraç bekler; virgül kullanmak
 * "hepsi tek sütunda" sonucunu doğurur ve muhasebeci dosyayı açamaz.
 */
export const AYRAC = ";";

export function csvUret(basliklar, satirlar) {
  const kacir = v => {
    const s = v == null ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };

  const govde = [
    basliklar.map(kacir).join(AYRAC),
    ...satirlar.map(r => r.map(kacir).join(AYRAC)),
  ].join("\r\n");

  // BOM olmadan Excel Türkçe karakterleri bozar.
  return "﻿" + govde;
}

const sube = branchId => (branchId ? subeBulHerYerde(branchId) : B());
const durumAdi = { odendi: "Ödendi", kismi: "Kısmi", bekliyor: "Bekliyor", gecikti: "Gecikti" };

// ---------- Aktarım tanımları ----------
//
// Her aktarım bir kimlik, etiket, gerekli yetki ve üretici taşır.
// Yetki alanı önemli: öğretmen ödeme listesini dışa aktaramamalı.

export const AKTARIMLAR = {
  ogrenciler: {
    label: "Öğrenci listesi",
    aciklama: "Resmî kuruma veya sigortaya gidecek döküm.",
    yetki: "ogrenciDosyaGor",
    uret: (branchId) => {
      const t = sube(branchId);
      return {
        basliklar: ["Ad Soyad", "Sınıf", "Doğum tarihi", "Yaş", "Cinsiyet",
          "Kayıt tarihi", "Program", "Servis", "Veli", "Telefon", "Alerjiler"],
        satirlar: t.students.map(s => {
          const v = veliler(s, branchId)[0];
          return [
            s.name,
            t.classes.find(c => c.id === s.classId)?.name ?? "",
            s.birthDate, age(s.birthDate), s.gender, s.enrolledAt,
            s.schedule === "yarim" ? "Yarım gün" : "Tam gün",
            s.service ? "Evet" : "Hayır",
            v?.name ?? "", v?.phone ?? "",
            alerjiler(s, branchId).map(a => a.name).join(", "),
          ];
        }),
      };
    },
  },

  odemeler: {
    label: "Ödeme dökümü",
    aciklama: "Muhasebeye gidecek dönemsel tahsilat listesi.",
    yetki: "odemeGor",
    uret: (branchId, { period = DONEM } = {}) => {
      const t = sube(branchId);
      const list = t.payments.filter(p => p.period === period);
      return {
        basliklar: ["Dönem", "Öğrenci", "Sınıf", "Tutar", "Tahsil edilen",
          "Kalan", "Durum", "Vade", "Tahsilat tarihi", "Yöntem", "Makbuz no", "Not"],
        satirlar: list.flatMap(p => {
          const s = t.students.find(x => x.id === p.studentId);
          if (!s) return [];
          const tutar = faturaTutari(s, p);
          const odenen = tahsilEdilen(p);
          const hareketler = p.transactions ?? [];
          const temel = [
            p.period, s.name,
            t.classes.find(c => c.id === s.classId)?.name ?? "",
            tutar, odenen, tutar - odenen,
            durumAdi[faturaDurumu(s, p)] ?? "", p.due,
          ];
          return hareketler.length
            ? hareketler.map(h => [...temel, h.date, h.method, h.receiptNo, p.note ?? ""])
            : [[...temel, "", "", "", p.note ?? ""]];
        }),
      };
    },
  },

  personel: {
    label: "Personel dökümü",
    aciklama: "Sigorta ve resmî bildirim için.",
    yetki: "personelDuzenle",
    uret: (branchId) => {
      const t = sube(branchId);
      return {
        basliklar: ["Ad Soyad", "Görev", "Sınıf", "Durum", "İşe başlama",
          "Telefon", "E-posta", "Mesai", "Eğitim", "Süresi dolan belge"],
        satirlar: t.staff.map(k => {
          const sorunlu = personelBelgeleri(k, branchId)
            .filter(b => belgeDurumu(b).durum === "doldu");
          return [
            k.name, k.role,
            t.classes.find(c => c.id === k.classId)?.name ?? "",
            k.status, k.startDate, k.phone, k.email, k.shift, k.education,
            sorunlu.map(b => b.name).join(", "),
          ];
        }),
      };
    },
  },

  yoklama: {
    label: "Devam çizelgesi",
    aciklama: "Dönemsel devamsızlık raporu — resmî kurum isteyebilir.",
    yetki: "yoklamaYaz",
    uret: (branchId, { period = DONEM } = {}) => {
      const t = sube(branchId);
      const ornek = aylikOzet(t, t.students[0] ?? { id: "", enrolledAt: BUGUN }, period);
      const gunler = ornek.gunler;
      return {
        basliklar: ["Öğrenci", "Sınıf", "Geldi", "İzinli", "Gelmedi", "Devam %",
          ...gunler.map(g => g.slice(8))],
        satirlar: t.students.map(s => {
          const o = aylikOzet(t, s, period);
          return [
            s.name,
            t.classes.find(c => c.id === s.classId)?.name ?? "",
            o.geldi, o.izinli, o.gelmedi, o.devamOrani ?? "",
            ...gunler.map(g => {
              const a = ogrenciKaydi(t, g, s.id);
              return a ? { geldi: "V", izinli: "İ", gelmedi: "X" }[a.status] ?? "" : "";
            }),
          ];
        }),
      };
    },
  },

  bordro: {
    label: "Bordro dökümü",
    aciklama: "Muhasebeye gidecek personel maliyeti.",
    yetki: "maasGor",
    uret: (branchId, { period = DONEM } = {}) => {
      const b = bordroUret(branchId, period);
      return {
        basliklar: ["Dönem", "Personel", "Görev", "Brüt", "Ek ödeme",
          "Kesinti", "Net", "İşveren maliyeti"],
        satirlar: b.items.map(x => [
          period, x.name, x.role, x.gross, x.toplamEk, x.toplamKesinti, x.net, x.isverenMaliyeti,
        ]),
      };
    },
  },

  giderler: {
    label: "Gider dökümü",
    aciklama: "Muhasebeye gidecek dönemsel gider listesi.",
    yetki: "giderOnayla",
    uret: (branchId, { period = DONEM } = {}) => ({
      basliklar: ["Tarih", "Kategori", "Alt kalem", "Açıklama", "Tedarikçi",
        "Vergi no", "Tutar", "KDV", "Yöntem", "Durum", "Fatura no"],
      satirlar: donemGiderleri(branchId, period).map(g => [
        g.date, g.category, g.subcategory, g.description,
        g.vendor?.name ?? "", g.vendor?.taxId ?? "",
        g.amount, g.vatAmount ?? Math.round(g.amount * (g.vatRate ?? 0)),
        g.paymentMethod, g.status, g.invoiceNo ?? "",
      ]),
    }),
  },
};

/** Bir aktarımı çalıştırıp indirilebilir dosya döner. */
export function aktarimUret(kod, branchId, secenekler = {}) {
  const tanim = AKTARIMLAR[kod];
  if (!tanim) throw new Error(`Bilinmeyen aktarım: ${kod}`);

  const bid = branchId ?? B().id;
  const { basliklar, satirlar } = subeBaglaminda(bid, () => tanim.uret(bid, secenekler));
  const t = sube(bid);
  const donem = secenekler.period ?? DONEM;

  return {
    dosyaAdi: `${dosyaSlug(t.name)}-${kod}-${donem}.csv`,
    icerik: csvUret(basliklar, satirlar),
    tur: "text/csv;charset=utf-8",
    satirSayisi: satirlar.length,
    label: tanim.label,
  };
}

const dosyaSlug = ad => ad
  .toLocaleLowerCase("tr-TR")
  .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ğ", "g")
  .replaceAll("ü", "u").replaceAll("ö", "o").replaceAll("ç", "c")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "");

export { dosyaSlug };
